/**
 * Tells a session credential from a scan credential, client-side, before either has been presented
 * to a route (docs/decisions.md D-57). Both mint the identical shape of short code - `/challenge`
 * and `/verify` do not change for this feature, so this app cannot tell the two apart from the URL
 * alone. What differs is the `aud` claim on the JWT `/verify` hands back.
 *
 * READING THE CLAIM HERE IS NOT A TRUST DECISION. This app decodes it without ever checking the
 * signature - it does not hold the key, and it must not pretend to - and uses it only to pick which
 * screen to render next. Every real request still goes through `ScanTokenFilter` or
 * `SessionTokenFilter` server-side, each of which accepts exactly one audience and validates the
 * signature properly. A forged or malformed token decoded here just routes to the wrong screen,
 * which then gets a 401 from the route that actually matters - it never reaches anything the server
 * has not independently re-checked.
 */
const SESSION_AUDIENCE = 'faceheart:session'

/** Whether `accessToken` is a session credential rather than a single-assessment scan credential. */
export function isSessionToken(accessToken: string): boolean {
  return decodeAudience(accessToken) === SESSION_AUDIENCE
}

function decodeAudience(token: string): string | null {
  const parts = token.split('.')
  if (parts.length !== 3) {
    return null
  }

  try {
    const payload = JSON.parse(base64UrlDecode(parts[1])) as { aud?: unknown }
    return typeof payload.aud === 'string' ? payload.aud : null
  } catch {
    return null
  }
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(segment.length / 4) * 4, '=')
  return atob(padded)
}
